import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import "@/admin.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pace_admin_token";

const getToken = () => localStorage.getItem(TOKEN_KEY);
const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const authHeader = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const apiErrorDetail = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return err?.message || "Something went wrong.";
};

// ─── Login Page ────────────────────────────────────────────────────────
export function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // If already authenticated, bounce to dashboard
    const token = getToken();
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: authHeader() })
      .then(() => navigate("/admin", { replace: true }))
      .catch(() => clearToken());
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        email: email.trim(),
        password,
      });
      setToken(data.access_token);
      toast.success("Welcome back", { description: data.user.email });
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(apiErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-shell admin-login-shell" data-testid="admin-login">
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0d1f3c",
            color: "#f5f0e8",
            border: "1px solid rgba(201,150,58,0.25)",
            fontFamily: "'DM Mono', monospace",
            fontSize: "0.78rem",
          },
        }}
      />
      <div className="admin-login-grid-lines" />
      <div className="admin-login-card">
        <div className="admin-login-eyebrow">— PACE Admin Console</div>
        <h1 className="admin-login-title">
          Sign in to <em>view submissions</em>
        </h1>
        <p className="admin-login-sub">
          Restricted access. Discovery call records are confidential and
          intended for PACE strategists only.
        </p>

        <form className="admin-login-form" onSubmit={handleSubmit} noValidate>
          <div className="pace-field">
            <label htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
            />
          </div>
          <div className="pace-field">
            <label htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          {error && (
            <div className="admin-login-error" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
            data-testid="admin-login-submit"
          >
            <span>{submitting ? "Signing in..." : "Sign In"}</span>
          </button>
        </form>

        <a href="/" className="admin-login-back" data-testid="admin-login-back">
          ← Back to public site
        </a>
      </div>
    </div>
  );
}

// ─── Dashboard Page ────────────────────────────────────────────────────
export function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Verify token + load
  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const meResp = await axios.get(`${API}/auth/me`, { headers: authHeader() });
        if (cancelled) return;
        setUser(meResp.data);
        const listResp = await axios.get(`${API}/discovery-calls`, {
          headers: authHeader(),
        });
        if (cancelled) return;
        setItems(listResp.data || []);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          clearToken();
          navigate("/admin/login", { replace: true });
        } else {
          toast.error("Failed to load", { description: apiErrorDetail(err) });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleLogout = () => {
    clearToken();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await axios.get(`${API}/discovery-calls/export.csv`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, 19);
      link.href = url;
      link.download = `pace-discovery-calls-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export ready", { description: "CSV downloaded" });
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Export failed", { description: apiErrorDetail(err) });
      }
    } finally {
      setExporting(false);
    }
  };

  const filtered = items.filter((it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (it.full_name || "").toLowerCase().includes(q) ||
      (it.email || "").toLowerCase().includes(q) ||
      (it.organization || "").toLowerCase().includes(q) ||
      (it.role || "").toLowerCase().includes(q) ||
      (it.focus_area || "").toLowerCase().includes(q) ||
      (it.message || "").toLowerCase().includes(q)
    );
  });

  // Stats
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const stats = items.reduce(
    (acc, it) => {
      const d = new Date(it.created_at);
      acc.total += 1;
      if (d >= weekAgo) acc.week += 1;
      if (d >= monthAgo) acc.month += 1;
      return acc;
    },
    { total: 0, week: 0, month: 0 }
  );

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0d1f3c",
            color: "#f5f0e8",
            border: "1px solid rgba(201,150,58,0.25)",
            fontFamily: "'DM Mono', monospace",
            fontSize: "0.78rem",
          },
        }}
      />

      <header className="admin-header">
        <div>
          <div className="admin-logo">
            PACE <span>Admin</span>
          </div>
          {user && (
            <div className="admin-user" data-testid="admin-user-email">
              Signed in as {user.email}
            </div>
          )}
        </div>
        <div className="admin-header-actions">
          <a href="/" className="btn-link" data-testid="admin-view-site">
            View Site
          </a>
          <button
            className="admin-btn-ghost"
            onClick={handleLogout}
            data-testid="admin-logout"
          >
            Sign Out
          </button>
        </div>
      </header>

      <main className="admin-main">
        <section className="admin-stats">
          <div className="admin-stat" data-testid="stat-total">
            <div className="admin-stat-num">{stats.total}</div>
            <div className="admin-stat-label">Total Submissions</div>
          </div>
          <div className="admin-stat" data-testid="stat-week">
            <div className="admin-stat-num">{stats.week}</div>
            <div className="admin-stat-label">Last 7 Days</div>
          </div>
          <div className="admin-stat" data-testid="stat-month">
            <div className="admin-stat-num">{stats.month}</div>
            <div className="admin-stat-label">Last 30 Days</div>
          </div>
        </section>

        <section className="admin-toolbar">
          <input
            className="admin-search"
            type="text"
            placeholder="Search name, email, organization, message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search"
          />
          <button
            className="btn-primary"
            onClick={handleExport}
            disabled={exporting || items.length === 0}
            data-testid="admin-export-csv"
          >
            <span>{exporting ? "Exporting..." : "Export CSV"}</span>
          </button>
        </section>

        <section className="admin-table-wrap">
          {loading ? (
            <div className="admin-empty" data-testid="admin-loading">
              Loading submissions...
            </div>
          ) : filtered.length === 0 ? (
            <div className="admin-empty" data-testid="admin-empty">
              {items.length === 0
                ? "No discovery call submissions yet."
                : "No submissions match your search."}
            </div>
          ) : (
            <table className="admin-table" data-testid="admin-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Organization</th>
                  <th>Role</th>
                  <th>Focus</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr
                    key={it.id}
                    onClick={() => setSelected(it)}
                    data-testid={`admin-row-${it.id}`}
                    className="admin-row"
                  >
                    <td className="admin-td-date">{formatDate(it.created_at)}</td>
                    <td>{it.full_name}</td>
                    <td>
                      <a
                        href={`mailto:${it.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="admin-email-link"
                      >
                        {it.email}
                      </a>
                    </td>
                    <td>{it.organization}</td>
                    <td className="admin-td-muted">{it.role || "—"}</td>
                    <td className="admin-td-muted">{it.focus_area || "—"}</td>
                    <td className="admin-td-msg">{it.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent
          className="admin-sheet"
          side="right"
          data-testid="admin-detail-sheet"
        >
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="admin-sheet-title">
                  {selected.full_name}
                </SheetTitle>
                <SheetDescription className="admin-sheet-desc">
                  Submitted {formatDate(selected.created_at)}
                </SheetDescription>
              </SheetHeader>
              <div className="admin-sheet-body">
                <div className="admin-detail-row">
                  <div className="admin-detail-label">Email</div>
                  <a
                    className="admin-detail-value admin-email-link"
                    href={`mailto:${selected.email}`}
                  >
                    {selected.email}
                  </a>
                </div>
                <div className="admin-detail-row">
                  <div className="admin-detail-label">Organization</div>
                  <div className="admin-detail-value">{selected.organization}</div>
                </div>
                <div className="admin-detail-row">
                  <div className="admin-detail-label">Role</div>
                  <div className="admin-detail-value">{selected.role || "—"}</div>
                </div>
                <div className="admin-detail-row">
                  <div className="admin-detail-label">Therapeutic Focus</div>
                  <div className="admin-detail-value">
                    {selected.focus_area || "—"}
                  </div>
                </div>
                <div className="admin-detail-row admin-detail-row-block">
                  <div className="admin-detail-label">Message</div>
                  <div
                    className="admin-detail-message"
                    data-testid="admin-detail-message"
                  >
                    {selected.message}
                  </div>
                </div>
                <div className="admin-detail-row">
                  <div className="admin-detail-label">Submission ID</div>
                  <div className="admin-detail-value admin-detail-id">
                    {selected.id}
                  </div>
                </div>
              </div>
              <div className="admin-sheet-actions">
                <a
                  href={`mailto:${selected.email}?subject=PACE Consulting — Discovery Call Follow-up`}
                  className="btn-primary"
                  data-testid="admin-detail-reply"
                >
                  <span>Reply via Email</span>
                </a>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Route guard
export function AdminGate() {
  const token = getToken();
  if (!token) return <Navigate to="/admin/login" replace />;
  return <AdminDashboard />;
}
